.editor__container {
  display: flex;
  flex-direction: column;
}

.editor__header {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  min-height: 1.5rem;
}

.editor__header-word-count {
  align-self: flex-end;
  font-size: 0.875rem;
  margin-left: 0.5rem;
  text-align: right;
  white-space: nowrap;
  width: 4.5rem;
}

.editor__header-word-count--alert {
  color: var(--color-red-600);
  font-weight: bold;
}

.editor {
  background-color: var(--color-uva-white);
  border: 1px solid var(--color-grey-600);
  border-radius: 4px;
  margin-bottom: 0.5rem;
  padding: 0.25rem;
}

.editor:has(.editor__field:focus-visible) {
  outline: 1px solid var(--color-navy-600);
}

.editor.not-valid {
  border-color: var(--color-red-600);
}

.editor__toolbar {
  align-items: center;
  display: flex;
  gap: 0.25rem;
  justify-content: flex-start;
  margin-bottom: 0.5rem;
  padding: 0.25rem;
}

.editor__toolbar-flyout-wrapper {
  background-color: var(--color-uva-white);
  border-radius: 0.25rem;
  box-shadow: 0 3px 6px rgba(0 0 0 / 40%);
  display: flex;
  flex-direction: column;
  left: 0;
  padding: 1rem;
  position: fixed;
  top: calc(100% + 0.25rem);
  z-index: 1500;
}

.editor__toolbar-button-group {
  align-items: center;
  display: flex;
  gap: 0.125rem;
  justify-content: center;
}

.editor__toolbar-button-group--text {
  align-items: center;
  display: flex;
  gap: 0.5rem;
  justify-content: center;
}

.editor__toolbar-button {
  align-items: center;
  background-color: var(--color-uva-white);
  border: none;
  border-radius: 0.25rem;
  cursor: pointer;
  display: flex;
  font-family: inherit;
  font-size: inherit;
  justify-content: center;
  min-height: 2rem;
  width: 2rem;
}

.editor__toolbar-button:hover {
  background-color: var(--color-grey-400);
  border: 1px solid var(--color-grey-700);
}

.editor__toolbar-button:focus-visible {
  outline: 0.125rem dotted var(--color-navy-600);
  outline-offset: 0.125rem;
  z-index: 1;
}

.editor__toolbar-button--active,
.editor__toolbar-button:active {
  background-color: var(--color-uva-black);
  border-color: var(--color-uva-black);
  color: var(--color-uva-white);
}

.editor__toolbar-button--text {
  border: 1px solid transparent;
  font-size: 1rem;
  padding: 0.25rem 0.5rem;
  width: auto;
}

.editor__toolbar-button:disabled {
  border-color: var(--color-grey-300);
  color: var(--color-grey-400);
  cursor: not-allowed;
}

.editor__toolbar-button:hover:disabled {
  background-color: var(--color-grey-300);
  border-color: var(--color-grey-300);
  color: var(--color-grey-500);
  cursor: not-allowed;
}

.editor__toolbar-button--bold {
  font-weight: bold;
}

.editor__toolbar-button--italic {
  font-style: italic;
}

.editor__toolbar-button:active:hover {
  color: var(--color-uva-white);
}

.editor__toolbar-button--active:hover {
  color: var(--color-uva-black);
}

.editor__toolbar-button--active:disabled {
  background-color: var(--color-grey-300);
  border-color: var(--color-grey-300);
  color: var(--color-grey-500);
  cursor: not-allowed;
}

.editor__toolbar-button-flyoutwrapper {
  position: relative;
}

.editor__field-wrapper {
  position: relative;
}

.editor__field-wrapper::before {
  border-top: 1px solid var(--color-grey-600);
  content: "";
  display: block;
  height: 0;
  margin-left: -0.25rem;
  margin-top: -0.3rem;
  width: calc(100% + 0.5rem);
}

.editor__field {
  background-color: var(--color-uva-white);
  margin-top: 0.5rem;
  min-height: 8rem;
  overflow-x: hidden;
  padding: 0 0.5rem 0.5rem;
  position: relative;
}

.editor__field::-webkit-scrollbar {
  cursor: default;
  width: 12px;
}

.editor__field::-webkit-scrollbar-track {
  background: var(--color-uva-white);
}

.editor__field::-webkit-scrollbar-thumb {
  background-color: var(--color-grey-500);
  border: 3px solid var(--color-uva-white);
  border-radius: 20px;
  cursor: default;
}

.editor__field:focus-visible {
  outline: none;
}

.editor__placeholder {
  color: var(--color-grey-500);
  left: 0.45rem;
  pointer-events: none;
  position: absolute;
  top: 0.55rem;
}

/* Editor theme blockstyles */

.guide-text-ltr {
  text-align: left;
}

.guide-text-rtl {
  text-align: right;
}

.guide-text-paragraph {
  margin: 0;
  position: relative;
}

.guide-text__text--bold {
  font-weight: bold;
}

.guide-text__text--italic {
  font-style: italic;
}

.guide-text__list--ul,
.guide-text__list--ol {
  list-style-position: inside;
  margin: 0 0 0 -0.25rem;
  padding: 0.25rem;
}

.guide-text__list--ol {
  list-style-position: outside;
  list-style-type: decimal;
  margin: 0 0 0 0.25rem;
}

.guide-text__list--ul .guide-text__list--listitem {
  margin: 0;
}

.guide-text__list--ol .guide-text__list--listitem {
  margin-left: 0.35rem;
  padding-left: 0.55rem;
}

.guide-text__list--ol .guide-text__list--nested-listitem,
.guide-text__list--ul .guide-text__list--nested-listitem {
  list-style-type: none;
  margin-left: 1.5rem;
}

.guide-text__list--ol .guide-text__list--nested-listitem {
  margin-left: 1rem;
}

.guide-text__list--ul2 .guide-text__list--listitem {
  list-style-type: circle;
}

.guide-text__list--ul3 .guide-text__list--listitem {
  list-style-type: square;
}

.guide-text__list--ul4 .guide-text__list--listitem {
  list-style-type: disc;
}

.guide-text__list--ul5 .guide-text__list--listitem {
  list-style-type: circle;
}

ul .guide-text__list--nested-listitem ol {
  list-style-position: outside;
  list-style-type: decimal;
  margin-left: 0;
  padding: 0.25rem 0.25rem 0.25rem 0.55rem;
}

ul .guide-text__list--nested-listitem ol .guide-text__list--listitem {
  list-style-type: decimal;
  margin-left: 0.35rem;
  padding-left: 0.55rem;
}

ol .guide-text__list--nested-listitem ul .guide-text__list--listitem {
  list-style-type: disc;
  margin-left: 0.35rem;
  padding-left: 0.55rem;
}

ul .guide-text__list--nested-listitem {
  list-style-type: none !important;
}

ol .guide-text__list--nested-listitem ul {
  margin-left: -1.5rem;
  padding-left: 0.55rem;
}

.guide-text__table-wrapper {
  margin: 0 1rem 0.5rem 0;
  overflow-x: auto;
}

.guide-text__table {
  border: 2px solid var(--color-grey-400);
  border-collapse: collapse;
  border-collapse: collapse;
  border-spacing: 0;
  margin: 0.5rem 0;
  overflow-x: scroll;
  overflow-y: scroll;
  table-layout: fixed;
  width: fit-content;
}

/* CSS counts the <colgroup> tag as the first "row" in the table. 
Therefore, to apply zebra striping to the actual odd rows, 
the CSS must target the even rows instead. */

.guide-text__table > tr:nth-child(even) {
  background-color: var(--color-grey-100);
}

.guide-text__table > tr td {
  border: 1px dotted var(--color-grey-400);
  padding: 0.25rem 0.5rem;
  position: relative;
  vertical-align: top;
}

td .guide-text__table-wrapper {
  margin: 0;
}

td:has(.guide-text__table-wrapper) {
  padding: 0 !important;
}

td:has(.guide-text__table-wrapper) table {
  border: 0;
  margin: 0;
}

td:has(.guide-text__table-wrapper) table tr {
  background-color: transparent !important;
}

.guide-text__course-catalogue-link {
  color: var(--color-grey-800);
  cursor: default;
  text-decoration: dotted underline;
}
